import type { ResponseOf, RouteDef } from '@pmai/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, call, type CallOptions } from './client';
import { useApiQuery } from './hooks';

const POLL_MS = 1000;

const isRunning = (state: unknown): boolean => (state as { running?: boolean } | undefined)?.running === true;

/**
 * A server job that runs in the background (Jira loads): `start` returns at once, then the state
 * is polled until the run is over — the mutation resolves with that final state, so the page
 * shows its result like any other action. `state` also keeps polling while a run started
 * elsewhere (another tab, before a reload) is going on.
 */
export function useBackgroundJob<S extends RouteDef, T extends RouteDef>(
  start: S,
  stateRoute: T,
  config: { invalidates?: readonly RouteDef[] } = {},
) {
  const queryClient = useQueryClient();
  const state = useApiQuery(
    stateRoute,
    {},
    { refetchInterval: (q) => (isRunning(q.state.data) ? POLL_MS : false) },
  );
  const run = useMutation<ResponseOf<T>, ApiError, CallOptions<S>>({
    mutationFn: async (opts) => {
      await call(start, opts);
      for (;;) {
        const now = await call(stateRoute);
        if (!isRunning(now)) return now;
        await new Promise((r) => setTimeout(r, POLL_MS));
      }
    },
    onSettled: async () => {
      await Promise.all(
        [stateRoute, ...(config.invalidates ?? [])].map((r) =>
          queryClient.invalidateQueries({ queryKey: [r.path] }),
        ),
      );
    },
  });
  return { state, run, running: run.isPending || isRunning(state.data) };
}
